import { useCallback, useEffect, useState } from 'react';

import { loadStoredBackgroundMode, storeBackgroundMode, useLiveKitBackgroundPreview } from '@zingizi/livekit-room';

interface LobbyBackgroundBlurResult {
  isBlurEnabled: boolean;
  toggleBlur: () => void;
  /** Браузер умеет размытие. Если false — контрол показывать не нужно. */
  isSupported: boolean;
  isPreparing: boolean;
  error: string | null;
}

/**
 * Размытие фона в лобби.
 *
 * Выбор пишется в localStorage тем же ключом, что и в звонке, поэтому отдельно
 * прокидывать его в сессию не нужно: useLiveKitVideoRoom восстанавливает фон
 * сам, как только появляется локальный трек.
 */
export function useLobbyBackgroundBlur(
  stream: MediaStream | null,
  videoRef: React.MutableRefObject<HTMLVideoElement | null>,
  isCameraEnabled = true,
): LobbyBackgroundBlurResult {
  const [isBlurEnabled, setIsBlurEnabled] = useState(() => loadStoredBackgroundMode()?.mode === 'blur');

  // Выключенная камера кадров не даёт: процессор на ней зацикливается на ошибке.
  const { previewStream, isSupported, isPreparing, error } = useLiveKitBackgroundPreview(
    stream,
    isBlurEnabled && isCameraEnabled,
  );

  const toggleBlur = useCallback(() => {
    setIsBlurEnabled((prev) => {
      const next = !prev;
      storeBackgroundMode(next ? 'blur' : 'none');

      return next;
    });
  }, []);

  // Пока процессор поднимается, показываем сырой поток — превью не должно моргать чёрным.
  useEffect(() => {
    if (!videoRef.current) return;

    videoRef.current.srcObject = previewStream ?? stream;
  }, [previewStream, stream, videoRef]);

  return { isBlurEnabled, toggleBlur, isSupported, isPreparing, error };
}
